import React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "../utils/cn";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      isLoading = false,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    const baseStyles =
      "inline-flex items-center justify-center font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60";

    const variantStyles = {
      primary:
        "bg-theme text-white hover:bg-primary-600 focus:ring-theme/30 dark:focus:ring-theme/50 shadow-md hover:shadow-lg",
      secondary:
        "bg-white dark:bg-gray-700 text-app dark:text-gray-100 border-2 border-app dark:border-gray-600 hover:bg-app-2 dark:hover:bg-gray-600 focus:ring-theme/20 dark:focus:ring-theme/30",
    };

    const sizeStyles = {
      sm: "h-9 px-4 text-sm",
      md: "h-10 px-5 text-base",
      lg: "h-11 px-6 text-lg",
    };

    return (
      <button
        ref={ref}
        type="button"
        disabled={isDisabled}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && "w-full",
          className
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin shrink-0 mr-2" />
            {children}
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
